import { useState, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { CHANNEL_ORDER, CHANNELS, type ChannelId } from "../../../shared/goals/channels";
import type { ChannelConfig, GoalConfig } from "../../../shared/goals/config";
import { useNative } from "../../../shared/phone/media";
import { Sheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import { failing, shownState, type ConnectionKind } from "../../lib/connectionsApi";
import { ChannelPill, useGoalConnections } from "../connections/ChannelPill";
import { WizardNav } from "./FlowHead";
import { andList } from "./text";

/** Save a change to the setup; resolves true once it is saved. The wizard shows the toast either way. */
export type SavePatch = (patch: Record<string, unknown>, ok: string) => Promise<boolean>;

const ICONS: Record<ChannelId, "bolt" | "mail"> = { meta: "bolt", email: "mail" };
const city = (m: string) => m.split(",")[0].trim();

function marketLine(markets: string[]) {
  const m = markets.map(city);
  if (!m.length) return "No market yet";
  return m.length === 1 ? `${m[0]} first, then the next market` : `${m[0]} first, then ${andList(m.slice(1))}`;
}

/** The channel's connection on a phone: connected, being checked, something to fix, or Connect. */
function CxState({ kind, cx }: { kind: ConnectionKind; cx: ReturnType<typeof useGoalConnections> }) {
  if (!cx.productId || !cx.conns) return null;
  const c = cx.conns.find((x) => x.kind === kind);
  const st = shownState(c);
  const to = `/connections?product=${encodeURIComponent(cx.productId)}`;
  if (st === "ok" || st === "warming")
    return (
      <span className="pn-pill good">
        <i />
        Connected
      </span>
    );
  if (st === "checking") return <span className="pn-pill">Checking</span>;
  if (st === "fix") {
    const n = Math.max(1, failing(c?.checks ?? []).length);
    return (
      <Link className="pn-btn sm soft su-cx warn" to={to}>
        {n === 1 ? "1 to fix" : `${n} to fix`}
      </Link>
    );
  }
  return (
    <Link className="pn-btn sm soft su-cx" to={to}>
      Connect
    </Link>
  );
}

function ChannelCard({ id, ch, skillItem, save, say, cx }: { id: ChannelId; ch: ChannelConfig; skillItem: string | null; save: SavePatch; say: (msg: string) => void; cx: ReturnType<typeof useGoalConnections> }) {
  const native = useNative();
  const def = CHANNELS[id];
  const card = ch.card ?? def.card;
  const mine = ch.card !== null;
  const [mode, setMode] = useState<"view" | "edit" | "rule">("view");
  const [rule, setRule] = useState("");
  const [lines, setLines] = useState({ for: "", own: "", ask: "" });
  const [mkOpen, setMkOpen] = useState(false);
  const [mk, setMk] = useState("");
  const set = (patch: Record<string, unknown>, ok: string) => save({ channels: { [id]: patch } }, ok);

  const sub = id === "email" ? (ch.markets.length ? `${city(ch.markets[0])} first` : "No market yet") : def.sub;

  const openEdit = () => {
    setLines({ for: [...card.for, ...ch.rules].join("\n"), own: card.own, ask: card.ask });
    setMode("edit");
  };
  const saveEdit = async () => {
    const next = { for: lines.for.split("\n").map((x) => x.trim()).filter(Boolean), own: lines.own.trim(), ask: lines.ask.trim() };
    const same = next.for.join("\n") === [...card.for, ...ch.rules].join("\n") && next.own === card.own && next.ask === card.ask;
    if (same) {
      say("Nothing changed.");
      setMode("view");
      return;
    }
    if (await set({ card: next, rules: [] }, "Saved for this goal. The plan follows it.")) setMode("view");
  };
  const saveRule = async () => {
    const v = rule.trim();
    if (!v) {
      say("Nothing to save.");
      setMode("view");
      return;
    }
    if (await set({ rules: [...ch.rules, v] }, "Saved for this goal. The plan follows it.")) {
      setRule("");
      setMode("view");
    }
  };
  const addMarket = async () => {
    const v = mk.trim().replace(/\s+/g, " ").slice(0, 40);
    if (v.length < 2) return;
    if (ch.markets.some((x) => x.toLowerCase() === v.toLowerCase())) {
      say(`${v} is already a market.`);
      return;
    }
    if (await set({ markets: [...ch.markets, v] }, `${v} added. The agent builds its list from people's own pages.`)) {
      setMk("");
      setMkOpen(false);
    }
  };
  const mkKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") void addMarket();
    if (e.key === "Escape") setMkOpen(false);
  };

  if (native) {
    const close = () => setMode("view");
    const skill = skillItem ? `/library/${skillItem}` : "/library";
    return (
      <>
        <section>
          <div className="pn-grp">
            <div className="pn-row static su-tall">
              <span className="pn-ico">
                <Icon name={ICONS[id]} />
              </span>
              <span className="m">
                <span className="t">{def.label}</span>
                <span className="s">{sub}</span>
              </span>
              {def.connection && <CxState kind={def.connection} cx={cx} />}
            </div>
          </div>
        </section>
        {id === "email" && (
          <section>
            <div className="pn-cap">Markets</div>
            <div className="pn-grp">
              {ch.markets.map((x, i) => (
                <div className="pn-row static" key={x}>
                  <span className="m">
                    <span className="t">{x}</span>
                    {i === 0 && <span className="s">First</span>}
                  </span>
                  <button type="button" className="su-x" aria-label={`Remove ${x}`} onClick={() => void set({ markets: ch.markets.filter((_, j) => j !== i) }, `${x} removed.`)}>
                    <Icon name="x" />
                  </button>
                </div>
              ))}
              <button type="button" className="pn-row su-acc" onClick={() => setMkOpen(true)}>
                <span className="pn-ico">
                  <Icon name="plus" />
                </span>
                <span className="m">
                  <span className="t">Add a market</span>
                </span>
              </button>
            </div>
          </section>
        )}
        <section>
          <div className="su-caph">
            <span>
              For this goal
              {mine && <span className="pn-pill violet">Your edits</span>}
            </span>
            <button type="button" className="pn-lnk" onClick={openEdit}>
              Edit
            </button>
          </div>
          <div className="pn-grp">
            {card.for.map((x, i) => (
              <div className="pn-row static su-full" key={`f${i}`}>
                <span className="su-dot" />
                <span className="m">
                  <span className="t">{x}</span>
                </span>
              </div>
            ))}
            {id === "email" && (
              <div className="pn-row static su-full">
                <span className="su-dot" />
                <span className="m">
                  <span className="t">{marketLine(ch.markets)}</span>
                </span>
              </div>
            )}
            {ch.rules.map((r, i) => (
              <div className="pn-row static su-full" key={`r${i}`}>
                <span className="su-dot mine" />
                <span className="m">
                  <span className="t">{r}</span>
                  <span className="s">Your rule</span>
                </span>
                <button type="button" className="su-x" aria-label="Remove this rule" onClick={() => void set({ rules: ch.rules.filter((_, j) => j !== i) }, "Rule removed.")}>
                  <Icon name="x" />
                </button>
              </div>
            ))}
            <button type="button" className="pn-row su-acc" onClick={() => setMode("rule")}>
              <span className="pn-ico">
                <Icon name="plus" />
              </span>
              <span className="m">
                <span className="t">Add a rule for this goal</span>
              </span>
            </button>
          </div>
        </section>
        <section>
          <div className="pn-grp">
            <div className="pn-row static su-tall">
              <span className="pn-ico good">
                <Icon name="spark" />
              </span>
              <span className="m">
                <span className="t">On its own</span>
                <span className="s su-wrap">{card.own}</span>
              </span>
            </div>
            <div className="pn-row static su-tall">
              <span className="pn-ico violet">
                <Icon name="user" />
              </span>
              <span className="m">
                <span className="t">Asks you first</span>
                <span className="s su-wrap">{card.ask}</span>
              </span>
            </div>
          </div>
          <p className="pn-note">
            Based on the{" "}
            <Link className="pn-lnk" to={skill}>
              {def.label} skill
            </Link>{" "}
            in your Library.
          </p>
        </section>
        <Sheet
          open={mode === "rule"}
          onClose={close}
          title="A rule for this goal"
          left={
            <button type="button" className="pn-tbtn" onClick={close}>
              Cancel
            </button>
          }
          right={
            <button type="button" className="pn-tbtn strong" onClick={() => void saveRule()}>
              Save
            </button>
          }
        >
          <div className="pn-form">
            <textarea className="pn-inp" rows={3} placeholder="For example: no ads on Sundays" aria-label="A rule for this goal" value={rule} onChange={(e) => setRule(e.target.value)} autoFocus />
          </div>
          <p className="pn-note">The plan follows it, and the agent checks every step against it.</p>
        </Sheet>
        <Sheet
          open={mode === "edit"}
          onClose={close}
          tall
          title={def.label}
          left={
            <button type="button" className="pn-tbtn" onClick={close}>
              Cancel
            </button>
          }
          right={
            <button type="button" className="pn-tbtn strong" onClick={() => void saveEdit()}>
              Save
            </button>
          }
        >
          <div className="pn-form">
            <div className="pn-field">
              <label htmlFor={`che-for-${id}`}>For this goal, one line each</label>
              <textarea className="pn-inp" id={`che-for-${id}`} rows={6} value={lines.for} onChange={(e) => setLines({ ...lines, for: e.target.value })} />
            </div>
            <div className="pn-field">
              <label htmlFor={`che-own-${id}`}>On its own</label>
              <textarea className="pn-inp" id={`che-own-${id}`} rows={3} value={lines.own} onChange={(e) => setLines({ ...lines, own: e.target.value })} />
            </div>
            <div className="pn-field">
              <label htmlFor={`che-ask-${id}`}>Asks you first</label>
              <textarea className="pn-inp" id={`che-ask-${id}`} rows={3} value={lines.ask} onChange={(e) => setLines({ ...lines, ask: e.target.value })} />
            </div>
          </div>
          {mine && (
            <div className="pn-grp su-gap">
              <button
                type="button"
                className="pn-row pn-act"
                onClick={() => {
                  close();
                  void set({ card: null }, "Back to the default for this channel.");
                }}
              >
                <span className="m">
                  <span className="t">Back to the default</span>
                </span>
              </button>
            </div>
          )}
        </Sheet>
        <Sheet
          open={mkOpen}
          onClose={() => setMkOpen(false)}
          title="Add a market"
          left={
            <button type="button" className="pn-tbtn" onClick={() => setMkOpen(false)}>
              Cancel
            </button>
          }
          right={
            <button type="button" className="pn-tbtn strong" disabled={mk.trim().length < 2} onClick={() => void addMarket()}>
              Add
            </button>
          }
        >
          <div className="pn-form">
            <input className="pn-inp" placeholder="A city or a state" aria-label="A city or a state" enterKeyHint="done" value={mk} onChange={(e) => setMk(e.target.value)} onKeyDown={mkKey} autoFocus />
          </div>
          <p className="pn-note">The agent builds its list from people's own pages there.</p>
        </Sheet>
      </>
    );
  }

  const head = (
    <div className="chc-h">
      <span className="ki">
        <Icon name={ICONS[id]} />
      </span>
      <div className="chc-t">
        <h3>{def.label}</h3>
        <span>{sub}</span>
      </div>
      <div className="chc-cx">
        {def.connection && <ChannelPill kind={def.connection} connected={def.connected} cx={cx} />}
      </div>
    </div>
  );

  if (mode === "edit")
    return (
      <section className="card chc editing" id={`ch-${id}`}>
        {head}
        <div className="chc-ed">
          <label className="fl" htmlFor={`che-for-${id}`}>
            For this goal <span className="fine">one line each</span>
          </label>
          <textarea className="field" id={`che-for-${id}`} rows={5} value={lines.for} onChange={(e) => setLines({ ...lines, for: e.target.value })} autoFocus />
          <div className="row2">
            <div>
              <label className="fl" htmlFor={`che-own-${id}`}>
                On its own
              </label>
              <textarea className="field" id={`che-own-${id}`} rows={3} value={lines.own} onChange={(e) => setLines({ ...lines, own: e.target.value })} />
            </div>
            <div>
              <label className="fl" htmlFor={`che-ask-${id}`}>
                Asks you first
              </label>
              <textarea className="field" id={`che-ask-${id}`} rows={3} value={lines.ask} onChange={(e) => setLines({ ...lines, ask: e.target.value })} />
            </div>
          </div>
          <div className="m-act">
            <button type="button" className="btn sm" onClick={() => void saveEdit()}>
              Save
            </button>
            <button type="button" className="btn sm ghost" onClick={() => setMode("view")}>
              Cancel
            </button>
          </div>
        </div>
      </section>
    );

  return (
    <section className="card chc" id={`ch-${id}`}>
      {head}
      <div className="chc-b">
        <div className="chc-for">
          {id === "email" && (
            <div className="chc-mk">
              <div className="wk-sub">Markets</div>
              <div className="mk-chips">
                {ch.markets.map((x, i) => (
                  <span className="chip mk" key={x}>
                    {x}
                    <button
                      type="button"
                      className="mk-x"
                      aria-label={`Remove ${x}`}
                      onClick={() => void set({ markets: ch.markets.filter((_, j) => j !== i) }, `${x} removed.`)}
                    >
                      <Icon name="x" />
                    </button>
                  </span>
                ))}
                {mkOpen ? (
                  <span className="mk-add">
                    <input className="field" placeholder="A city or a state" aria-label="A city or a state" value={mk} onChange={(e) => setMk(e.target.value)} onKeyDown={mkKey} autoFocus />
                    <button type="button" className="btn sm" onClick={() => void addMarket()}>
                      Add
                    </button>
                    <button type="button" className="btn sm ghost" onClick={() => setMkOpen(false)}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button type="button" className="chip link mk-plus" onClick={() => setMkOpen(true)}>
                    <Icon name="plus" />
                    Add a market
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="wk-sub">
            For this goal
            {mine && (
              <span className="chip rl" style={{ marginLeft: 8 }}>
                <Icon name="pen" />
                Your edits
              </span>
            )}
          </div>
          <ul>
            {card.for.map((x, i) => (
              <li key={`f${i}`}>{x}</li>
            ))}
            {id === "email" && <li>{marketLine(ch.markets)}</li>}
            {ch.rules.map((r, i) => (
              <li className="mine" key={`r${i}`}>
                <span>{r}</span>
                <button type="button" className="chc-rm" aria-label="Remove this rule" onClick={() => void set({ rules: ch.rules.filter((_, j) => j !== i) }, "Rule removed.")}>
                  <Icon name="x" />
                </button>
              </li>
            ))}
          </ul>
          {mode === "rule" ? (
            <div className="chc-add">
              <label className="fl" htmlFor={`chr-${id}`}>
                A rule for this goal
              </label>
              <textarea className="field" id={`chr-${id}`} rows={2} placeholder="For example: no ads on Sundays" value={rule} onChange={(e) => setRule(e.target.value)} autoFocus />
              <div className="m-act">
                <button type="button" className="btn sm" onClick={() => void saveRule()}>
                  Save
                </button>
                <button type="button" className="btn sm ghost" onClick={() => setMode("view")}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="chc-acts">
              <button type="button" className="chip link chc-plus chc-edit" onClick={openEdit}>
                <Icon name="pen" />
                Edit
              </button>
              <button type="button" className="chip link chc-plus chc-addr" onClick={() => setMode("rule")}>
                <Icon name="plus" />
                Add a rule for this goal
              </button>
              {mine && (
                <button type="button" className="wk-undo" onClick={() => void set({ card: null }, "Back to the default for this channel.")}>
                  Undo my edit
                </button>
              )}
            </div>
          )}
        </div>
        <div className="chc-who">
          <div className="chc-w">
            <span className="chc-wl">
              <Icon name="spark" />
              On its own
            </span>
            <p>{card.own}</p>
          </div>
          <div className="chc-w ask">
            <span className="chc-wl">
              <Icon name="user" />
              Asks you first
            </span>
            <p>{card.ask}</p>
          </div>
        </div>
      </div>
      <div className="chc-f">
        <Link className="chc-sk" to={skillItem ? `/library/${skillItem}` : "/library"}>
          Based on the {def.label} skill
          <Icon name="right" />
        </Link>
      </div>
    </section>
  );
}

/** The channel step's title: "How Meta ads will run", or for a goal from before one channel per goal, "How each channel will run". */
export function channelTitle(config: GoalConfig | null): string {
  const on = CHANNEL_ORDER.filter((k) => config?.channels[k]?.on);
  if (on.length !== 1) return "How each channel will run";
  return `How ${on[0] === "meta" ? CHANNELS.meta.label : CHANNELS[on[0]].label.toLowerCase()} will run`;
}

/** Step 2, the channel: the goal's channel card, in the owner's words, with this goal's rules. The playbook is written for it next. */
export function ChannelsStep({
  config,
  save,
  say,
  onBack,
  onNext,
  busy,
  next = "Draft the plan",
  head,
}: {
  config: GoalConfig;
  save: SavePatch;
  say: (msg: string) => void;
  onBack: () => void;
  onNext: () => void;
  busy: boolean;
  /** The next step's button: Edit goal ends on its changes or a new plan instead. */
  next?: string;
  /** On a phone and an iPad: the step's heading. */
  head?: ReactNode;
}) {
  const native = useNative();
  const used = CHANNEL_ORDER.filter((k) => config.channels[k]?.on);
  const cx = useGoalConnections();
  const cards = used.map((k) => (
    <ChannelCard
      key={k}
      id={k}
      ch={config.channels[k]}
      skillItem={config.library.find((p) => p.kind === "skill" && p.name === CHANNELS[k].skill)?.itemId ?? null}
      save={save}
      say={say}
      cx={cx}
    />
  ));
  if (native)
    return (
      <>
        <div className="pn-stack su-step">
          {head}
          {cards}
        </div>
        <WizardNav next={next} busy={busy} onNext={onNext} />
      </>
    );
  return (
    <>
      <div className="chs" id="chCards">
        {cards}
      </div>
      <WizardNav onBack={onBack} next={next} busy={busy} onNext={onNext} />
    </>
  );
}
